<template>
    <div class="referring-platform-grant" v-show="loading || referringPlatform.uuid || referringPlatform._uuid">
        <page-loader v-if="loading || !referringPlatform.uuid && !referringPlatform._uuid" :loading="true"/>
        <full-screen-dialog
            v-else
            :header-close-button-disabled="headerCloseButtonDisabled"
            header-back-button-disabled
            @close="handleClose"
        >
            <b-row align-h="center">
                <b-col
                    cols="12"
                    lg="8"
                >
                    <div class="card card-default">
                        <div class="card-header">
                            Authorization Request
                        </div>
                        <div class="card-body">
                            <!-- Introduction -->
                            <p><strong>{{ referringPlatform.displayName || referringPlatformSlug }}</strong> is
                                requesting permission to access your account.</p>

                            <!-- Scope List -->
                            <div v-if="(referringPlatform.scopes || []).length" class="scopes">
                                <p><strong>This application will be able to:</strong></p>

                                <ul>
                                    <li v-if="scope in referringPlatform.scopes">{{ scope.description }}</li>
                                </ul>
                            </div>

                            <div class="buttons">
                                <!-- Authorize Button -->
                                <button class="btn btn-success btn-approve" @click="() => referringPlatformGrant()">
                                    Authorize
                                </button>

                                <!-- Cancel Button -->
                                <button class="btn btn-danger" @click="() => handleClose()">Cancel</button>
                            </div>
                        </div>
                    </div>
                </b-col>
            </b-row>
        </full-screen-dialog>
    </div>
</template>

<script>
import {mapActions, mapState} from 'pinia';
import {useAuthStore} from '@store';
import {isAgent} from "@helpers/user"
import ShowNotificationMixin from "@sellers-shield-home-protection/ss-ui-kit/src/mixins/ShowNotificationMixin";
import ReferringPlatformAppResource
    from '@sellers-shield-home-protection/ss-core-js/src/AppResources/ReferringPlatformAppResource';
import {pageRedirectMainLogout, proxyRedirectUrlByV2} from "@helpers/router-helper";
import {getActiveThirdPartyPlatformList} from "@helpers/api-helper";
import SetupEngineAxiosMixin from "@packages/integrations/mixins/SetupEngineAxiosMixin";

const referringPlatformUnknown = 'The referring platform that is requesting authorization isn\'t registered with sellers shield!';

export default {
    name: "ReferringPlatformGrantModal",
    props: {
        headerCloseButtonDisabled: {
            type: Boolean,
            default: true,
        },
        referringPlatformSlug: {
            type: String,
            required: true,
        },
        authCallbackUrl: {
            type: String,
            required: true,
        },
        email: {
            type: String,
            default: '',
        },
    },
    mixins: [
        ShowNotificationMixin,
        SetupEngineAxiosMixin
    ],
    data: () => ({
        loading: false,
        referringPlatform: new ReferringPlatformAppResource({}),
    }),
    computed: {
        ...mapState(useAuthStore, {
            me: 'me',
        }),
    },
    methods: {
        ...mapActions(useAuthStore, {
            thirdPartyAuthorize: 'thirdPartyAuthorize',
        }),
        async fetchCurrentReferringPlatform() {
            this.referringPlatform = (await getActiveThirdPartyPlatformList()).find(p => p?.slug === this.referringPlatformSlug) || {};
        },
        async referringPlatformGrant() {
            let response = this.referringPlatform.clientUuid && this.authCallbackUrl
                ? await this.thirdPartyAuthorize(
                    this.referringPlatform.clientUuid,
                    this.authCallbackUrl,
                )
                : {};

            if (!!response.redirectUrl) {
                if (!!response.shouldRedirect) {
                    window.location.href = proxyRedirectUrlByV2(response.redirectUrl);
                } else if(this.authCallbackUrl === '#') {
                    // edge cases were the call from 3rd parties don't have authCallbackUrl set but the user re-activated the old access token
                    window.location.href = window.location.origin + '/dashboard';
                }
            } else {
                !response.message || this.showNotification({
                    type: 'error',
                    message: response.message ?? referringPlatformUnknown
                });

                await this.handleClose();
            }
        },
        async handleClose() {
            await pageRedirectMainLogout();
        },
    },
    async created() {
        let error = null;
        this.loading = true;

        if (isAgent(this.me)) {
            if (!!this.email && this.email != this.me.email) {
                error = `Somehow the logged-in user (${this.me.email}) is different than the agent account we're trying to link to ${this.referringPlatform?.slug} (${this.email})!
                This is probably because the user signed into a different account in a new browser tab.`
            }
        } else {
            //we only allow agent account for not to link referring platforms
            error = true;
        }
        if (!error) {
            await this.fetchCurrentReferringPlatform();
            !!this.referringPlatform.clientUuid || (error = referringPlatformUnknown);
        }
        if (error) {
            error === true || this.showNotification({
                type: 'error',
                message: error
            });
            await this.handleClose();
        }
        this.loading = false;
    },
}
</script>

<style lang="scss" scoped>
.referring-platform-grant {
    .full-screen-dialog {
        min-height: calc(100vh - 20rem);

        .container {
            margin-top: 1.875rem;
        }

        .scopes {
            margin-top: 1.25rem;
        }

        .buttons {
            margin-top: 1.6rem;
            text-align: center;
        }

        .btn {
            width: 7.813rem;
        }

        .btn-approve {
            margin-right: 0.938rem;
        }
    }

    form {
        display: inline;
    }
}
</style>